import config from '@/config'
import { clearPersistedCache } from '@/lib/queryPersister'
import { queryClient } from '@/main'
import { updateApiClient } from '@/stores/api'
import { destroyStoredRedirect, getStoredRedirect } from '@/stores/redirect'
import { setSessionToken } from '@/stores/session'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

// Cache of in-flight exchange requests, keyed by code. Two reasons:
//   1. TanStack Router's beforeLoad can fire more than once (React Strict Mode
//      in dev, route re-entry edge cases). The backend treats codes as
//      single-use and deletes them on first lookup — without this cache, the
//      second beforeLoad call would hit a now-consumed code and 401.
//   2. The same code on a quick re-render resolves to the same sessionToken
//      without a duplicate network round-trip.
// Entries are cleared shortly after resolution so a future page load with a
// different code starts fresh.
const inflightExchange: Map<string, Promise<string>> = new Map()

async function exchangeCodeForToken(code: string): Promise<string> {
  const existing = inflightExchange.get(code)
  if (existing) return existing

  const promise = (async () => {
    const res = await fetch(`${config.backendURL}/auth/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    })
    if (!res.ok) {
      throw new Error(`Failed to exchange auth code: ${res.status}`)
    }
    const body: { sessionToken: string } = await res.json()
    return body.sessionToken
  })()

  inflightExchange.set(code, promise)
  promise.finally(() => {
    setTimeout(() => inflightExchange.delete(code), 10_000)
  })

  return promise
}

export const Route = createFileRoute('/process-login')({
  beforeLoad: async ({ search }) => {
    const parsed = z.object({ code: z.string() }).parse(search)

    const sessionToken = await exchangeCodeForToken(parsed.code)

    // Every genuinely-new sign-in flows through here (the code→token exchange); a reload with a still-valid
    // token does not. Drop any cache the previous user left behind — logout clears it, but a session that
    // simply expired does not, so on a shared machine the incoming user could otherwise rehydrate the prior
    // user's persisted CRT data. Clearing here keys the cache to the user without needing a per-user key.
    queryClient.clear()
    await clearPersistedCache()

    setSessionToken(sessionToken)
    updateApiClient(sessionToken)

    const storedRedirect = getStoredRedirect()

    if (storedRedirect) {
      destroyStoredRedirect()

      throw redirect({
        to: storedRedirect.path,
        search: storedRedirect.search,
      })
    }

    // No explicit redirect: the index route picks the welcome page of a module the user can access.
    throw redirect({ to: '/' })
  },

  component: RouteComponent,
})

function RouteComponent() {
  const { t } = useTranslation('login')

  return <div>{t("Wait a moment while we're setting up the table...")}</div>
}
